import { useDashboardContext } from './DashboardContext';
import moment from 'moment';
import CrossFade from './CrossFade';
import { Icon, Icons } from '@silverfort/sf-components';
import { FC } from 'react';
import { ModuleData } from './Types';

const ModuleInfo: FC = () => {
    const { currentModule, licenceExpiration } = useDashboardContext();

    let content: React.ReactNode;
    let identity: string;

    if (licenceExpiration?.expiresInHours && licenceExpiration.expiresInHours <= licenceExpiration.threshold) {
        content = <LicenceState licenceExpirationInHours={licenceExpiration.expiresInHours} />;
        identity = 'license';
    } else if (currentModule === null || currentModule.isDisabled) {
        content = <Icon icon={Icons.SilverfortIconOrange} useSourceSize />;
        identity = 'icon';
    } else {
        content = <InfoState currentModule={currentModule} />;
        identity = currentModule.title;
    }

    return (
        <div className="absolute top-[3%] left-1/2 -translate-x-1/2 h-[12.5vh] w-full flex items-center justify-center py-[1.44vh]">
            <CrossFade content={content} identity={identity} />
        </div>
    );
};

interface InfoStateProps {
    currentModule: ModuleData;
}

const InfoState: FC<InfoStateProps> = ({ currentModule }) => {
    return (
        <div className="flex flex-col gap-[0.96vh]">
            <div className="text-transparent bg-clip-text text-center text-[2.52vh] font-medium bg-gradient-to-r from-sf-sunset-200 to-sf-sunset-300 font-['Bitter']">
                {currentModule.title}
            </div>
            <div className="text-sf-white text-[1.56vh] font-normal text-center">{currentModule.info.summary}</div>
        </div>
    );
};

interface LicenceStateProps {
    licenceExpirationInHours: number;
}

const LicenceState: FC<LicenceStateProps> = ({ licenceExpirationInHours }) => {
    const expireDate = moment().add(licenceExpirationInHours, 'hours');
    const daysCount = Math.floor(licenceExpirationInHours / 24);
    const expireText =
        licenceExpirationInHours >= 24
            ? `${daysCount} day${daysCount === 1 ? '' : 's'}`
            : `${licenceExpirationInHours} hour${licenceExpirationInHours === 1 ? '' : 's'}`;

    return licenceExpirationInHours > 0 ? (
        <div className="w-fit font-sf text-sf-white font-normal text-13 border border-sf-neutral-500 bg-[#443308] h-fit px-4 py-2 rounded-lg">
            <span>
                {`License will expire in approximately ${expireText}, valid until ${expireDate.format(
                    'dddd, MMMM Do YYYY'
                )}. Update the license key in `}
                <a href="/Settings/Licensing" className="text-sf-sunset-200">
                    Settings Page
                </a>
                .
            </span>
        </div>
    ) : (
        <div className="w-fit font-sf text-sf-white font-normal text-13 border border-sf-negative-500 bg-[#510D15] h-fit px-4 py-2 rounded-lg">
            {`License expired! Update the license key in `}
            <a href="/Settings/Licensing" className="text-sf-sunset-200">
                Settings Page
            </a>
            .
        </div>
    );
};

export default ModuleInfo;
